/**
 * JoinAddContent — the body of the [+ add join] popover. Two modes:
 *
 *  - list: a searchable `cmdk` Command with up to four groups — real-FK
 *    candidates and loose-relationship candidates (MetaInfo declarations,
 *    badged "Loose FK" so they never read as a constraint; select → commit an
 *    INNER JOIN with the auto-derived ON immediately), every other table
 *    (select → drill into the column picker because there's no FK to derive
 *    an ON from), and, last, a "Self-join" group for tables already in the
 *    query (joining one to itself under a new alias).
 *  - on-form: the chosen non-FK table's `JoinOnColumnForm`; submitting commits
 *    a manual INNER JOIN with the picked columns. The left side can reference
 *    ANY table the statement already reads (grouped), not just the leading
 *    FROM item — never a table it doesn't.
 *
 * The Command lives inline (the surrounding Popover is owned by JoinAddPopover).
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove JOIN.
 */

import { useMemo, useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../../../components/ui/command';
import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import {
  lookupRelationColumns,
  reachableRefersTo,
  reachableRelation,
  relationKey,
  resolvedRelationsForMatching,
  sameRelation,
} from '../../../../sql-resolver/service/snapshotRelations';
import type { Column, JoinCandidate, SchemaSnapshot, Table } from '../../../../sql-resolver/types/schema';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext, toCommitCtx } from '../../edit/editContext';
import { addJoinClause, addJoinFromCandidate } from '../../edit/edits/addJoin';
import { inQueryRelations } from '../../edit/edits/joinHelpers';
import { querySources, type QuerySource } from '../../../../sql-resolver/scope/querySources';
import { tableSuggestionKey, tableSuggestionLabel } from '../../edit/edits/fromItemHelpers';
import {
  buildPlainJoinClause,
  derivePlainJoinAlias,
  pickSmartJoinColumns,
} from '../../edit/edits/plainJoin';
import { columnsToGroups } from '../common/columnGroups';
import { JoinCandidateGroups } from './JoinCandidateGroups';
import { JoinOnColumnForm } from './JoinOnColumnForm';

export interface JoinAddContentProps {
  readonly testId: string;
  readonly editContext: VqeEditContext;
  readonly leftTable: QualifiedName;
  readonly leftAlias: string;
  readonly candidates: readonly JoinCandidate[];
  readonly tables: readonly Table[];
  readonly snapshot: SchemaSnapshot;
  readonly loading: boolean;
  readonly onClose: () => void;
}

export function JoinAddContent({
  testId,
  editContext,
  leftTable,
  leftAlias,
  candidates,
  tables,
  snapshot,
  loading,
  onClose,
}: JoinAddContentProps) {
  void leftTable;
  const [picked, setPicked] = useState<Table | null>(null);
  const engine = (editContext.service?.engineId ?? snapshot.engineId) as SqlEngine;

  // The statement's structure — relations already in its FROM / JOIN (with
  // their aliases). `scope.reachableTables` is not a reliable "in the query"
  // signal (it can carry the whole schema, or nothing at statement start), so
  // the self-join split and the ON form's left side read the structure.
  const structure = useMemo(() => {
    try {
      return extractStructureForContext(editContext);
    } catch {
      return null;
    }
  }, [editContext]);
  // Resolved to the snapshot relations they name (schema included — joining
  // `mia_s2.orders` to `mia_s1.orders` is not a self-join).
  const inQuery = useMemo(
    () => resolvedRelationsForMatching(snapshot, inQueryRelations(structure, engine)),
    [structure, engine, snapshot],
  );
  const sources = useMemo(() => querySources(structure, engine, { snapshot }), [structure, engine, snapshot]);

  // Tables that already surface as an FK candidate shouldn't repeat in the
  // "other tables" (manual-ON) list; tables already in the query move to their
  // own "Self-join" group at the end.
  const { otherTables, selfJoinTables } = useMemo(() => {
    const fkKeys = new Set(candidates.map((c) => tableSuggestionKey(c.rightTable)));
    const isInQuery = (t: Table): boolean => inQuery.some((r) => sameRelation(r.qualified, t.qualified));
    const nonFk = tables.filter((t) => !fkKeys.has(tableSuggestionKey(t)));
    return {
      otherTables: nonFk.filter((t) => !isInQuery(t)),
      selfJoinTables: nonFk.filter(isInQuery),
    };
  }, [candidates, tables, inQuery]);

  if (picked) {
    const isSelfJoin = inQuery.some((r) => sameRelation(r.qualified, picked.qualified));
    // Left side spans every table the statement reads EXCEPT the one being
    // joined; right side is the table being added. Filtering out the join
    // target prevents nonsensical self-pairs like `ARD.ARDID = ard.ARDID`. A
    // self-join is the exception: the left side is the instance(s) already in
    // the query, under their existing aliases, and the right side is the new
    // instance. Without a readable statement, the scope's tables stand in.
    const leftColumns = isSelfJoin
      ? inQuery.flatMap((r) => lookupRelationColumns(snapshot, r.qualified) ?? [])
      : structure
        ? leftSourceColumns(sources, snapshot, picked.qualified)
        : (editContext.scope?.reachableTables ?? [])
            .filter((rt) => !reachableRefersTo(rt, picked.qualified)) // never offer the table being joined on the left
            .flatMap((rt) => lookupRelationColumns(snapshot, reachableRelation(rt)) ?? []);
    const rightColumns = lookupRelationColumns(snapshot, picked.qualified) ?? [];
    const used = new Set([
      ...(editContext.scope?.aliases ?? []).map((a) => a.alias),
      ...inQuery.flatMap((r) => (r.alias ? [r.alias] : [])),
    ]);
    const alias = derivePlainJoinAlias(picked.qualified.name, used);
    // Headings name each source as the query does (alias, else table); the
    // options' values are the dialect-correct SQL references.
    const leftGroups = columnsToGroups(leftColumns, { engine, sources });
    const defaults = pickSmartJoinColumns(leftColumns, picked, rightColumns);
    const defaultLeftQualified = defaults
      ? leftGroups.flatMap((g) => g.items).find((i) => i.column === defaults.leftColumn)?.sqlRef
      : undefined;
    return (
      <div className="p-2">
        <JoinOnColumnForm
          testId={testId}
          rightLabel={alias}
          leftGroups={leftGroups}
          rightColumns={rightColumns}
          defaultLeftQualified={defaultLeftQualified}
          defaultRightColumn={defaults?.rightColumn}
          onBack={() => setPicked(null)}
          onSubmit={({ leftQualified, rightColumn }) => {
            void addJoinClause(
              buildPlainJoinClause({ rightTable: picked, rightAlias: alias, leftQualified, rightColumn, engine }),
              toCommitCtx(editContext),
            );
            onClose();
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <p className="p-3 italic text-muted-foreground" data-testid={`${testId}-loading`}>
        Loading suggestions…
      </p>
    );
  }

  if (candidates.length === 0 && otherTables.length === 0 && selfJoinTables.length === 0) {
    return (
      <p className="p-3 italic text-muted-foreground" data-testid={`${testId}-empty`}>
        No other tables in scope to join. Refresh the schema or pick a database with tables.
      </p>
    );
  }

  return (
    <Command data-testid={`${testId}-list`}>
      <CommandInput placeholder="Search tables to join…" data-testid={`${testId}-search`} className="h-8" />
      <CommandList>
        <CommandEmpty data-testid={`${testId}-no-match`}>No matching tables.</CommandEmpty>
        <JoinCandidateGroups
          testId={testId}
          candidates={candidates}
          onPick={(c) => {
            void addJoinFromCandidate(c, leftAlias, toCommitCtx(editContext));
            onClose();
          }}
        />
        {otherTables.length > 0 ? (
          <CommandGroup heading={candidates.length > 0 ? 'Other tables' : 'Tables'}>
            {otherTables.map((t) => (
              <CommandItem
                key={tableSuggestionKey(t)}
                value={`${tableSuggestionLabel(t)} ${tableSuggestionKey(t)}`}
                data-testid={`${testId}-item-${tableSuggestionKey(t)}`}
                onSelect={() => setPicked(t)}
                className="gap-2"
              >
                <span className="flex-1 truncate font-mono">{tableSuggestionLabel(t)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {selfJoinTables.length > 0 ? (
          <CommandGroup heading="Self-join" data-testid={`${testId}-self-join-group`}>
            {selfJoinTables.map((t) => (
              <CommandItem
                key={tableSuggestionKey(t)}
                value={`self-join ${tableSuggestionLabel(t)} ${tableSuggestionKey(t)}`}
                data-testid={`${testId}-item-${tableSuggestionKey(t)}`}
                onSelect={() => setPicked(t)}
                className="gap-2"
              >
                <span className="flex-1 truncate font-mono">{tableSuggestionLabel(t)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </Command>
  );
}

/**
 * The columns of every base table the statement reads, except the join
 * target — once per table (by relation identity, schema included), from the
 * snapshot. `sources` are resolved against it (`querySources({ snapshot })`).
 */
function leftSourceColumns(
  sources: readonly QuerySource[],
  snapshot: SchemaSnapshot,
  target: QualifiedName,
): Column[] {
  const seen = new Set<string>();
  const out: Column[] = [];
  for (const s of sources) {
    const relation = s.kind === 'relation' ? s.relation : null;
    if (!relation || sameRelation(relation, target) || seen.has(relationKey(relation))) continue;
    seen.add(relationKey(relation));
    out.push(...(lookupRelationColumns(snapshot, relation) ?? []));
  }
  return out;
}
